// Technology.js - Adam International Academy Academic Technology Solutions
import React, { useState, useEffect, useRef } from 'react';
import './Technology.css';
import { Link } from 'react-router-dom';

const Technology = () => {
  const [activeTab, setActiveTab] = useState('elearning');
  const [selectedTech, setSelectedTech] = useState(null);
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px' }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Academic Technology Categories
  const techCategories = [
    { id: 'elearning', name: 'e-Learning Platform', icon: '💻', description: 'Multilingual learning management system for academic programs' },
    { id: 'ai', name: 'AI in Education', icon: '🤖', description: 'Artificial intelligence applications for personalized learning' },
    { id: 'analytics', name: 'Learning Analytics', icon: '📊', description: 'Data-driven insights for educational outcomes' },
    { id: 'mobile', name: 'Mobile Learning', icon: '📱', description: 'Mobile-first learning applications with offline access' },
    { id: 'virtual', name: 'Virtual Classrooms', icon: '🎥', description: 'Interactive online learning environments' }
  ];

  // Academic Technology Solutions
  const techSolutions = {
    elearning: [
      {
        id: 1,
        title: 'Adam Learning Management System (LMS)',
        description: 'A comprehensive multilingual e-learning platform designed for academic institutions. Supports self-paced learning, blended learning, and MOOCs with SDG-aligned content.',
        image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'Multilingual interface (English, Arabic, Turkish)',
          'Self-paced and blended learning modes',
          'Mobile learning support',
          'Student progress tracking',
          'Certificate generation',
          'SDG-aligned content integration'
        ],
        stats: { institutions: '50+', courses: '500+', students: '15K+', languages: '3' },
        applications: ['Universities', 'Training Centers', 'Corporate Learning']
      },
      {
        id: 2,
        title: 'Virtual Classroom Platform',
        description: 'Interactive virtual classroom with real-time collaboration tools for synchronous learning and academic instruction.',
        image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'HD video conferencing',
          'Interactive whiteboard',
          'Breakout rooms',
          'Screen sharing',
          'Session recording',
          'Real-time polls and quizzes'
        ],
        stats: { sessions: '10K+', instructors: '200+', rating: '4.8/5', countries: '25+' },
        applications: ['Online Classes', 'Academic Webinars', 'Workshops']
      }
    ],
    ai: [
      {
        id: 3,
        title: 'AI-Powered Personalized Learning',
        description: 'Intelligent tutoring system that adapts to individual learning styles and provides personalized academic support.',
        image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'Adaptive learning paths',
          'Personalized recommendations',
          'Performance analytics',
          'Learning style assessment',
          '24/7 academic support',
          'Multi-subject coverage'
        ],
        stats: { students: '8K+', accuracy: '92%', satisfaction: '94%', subjects: '25+' },
        applications: ['Personalized Education', 'Remedial Learning', 'Academic Support']
      },
      {
        id: 4,
        title: 'Automated Assessment & Feedback System',
        description: 'AI-driven system for automated grading, personalized feedback, and academic performance analysis.',
        image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'Automated grading',
          'Personalized feedback',
          'Plagiarism detection',
          'Performance analytics',
          'Rubric-based assessment',
          'Progress tracking'
        ],
        stats: { assessments: '50K+', timeSaved: '70%', accuracy: '95%', institutions: '30+' },
        applications: ['Assessment Automation', 'Feedback Systems', 'Academic Evaluation']
      }
    ],
    analytics: [
      {
        id: 5,
        title: 'Learning Analytics Dashboard',
        description: 'Comprehensive analytics solution for tracking student progress, predicting outcomes, and optimizing academic programs.',
        image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'Real-time data visualization',
          'Predictive analytics',
          'Student performance tracking',
          'Engagement metrics',
          'Custom reporting',
          'Export capabilities'
        ],
        stats: { metrics: '50+', institutions: '40+', insights: '85% actionable', accuracy: '89%' },
        applications: ['Academic Analytics', 'Student Success', 'Program Evaluation']
      },
      {
        id: 6,
        title: 'Academic Performance Predictor',
        description: 'AI-driven system that predicts student performance and identifies at-risk students for early intervention.',
        image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'Early warning system',
          'Risk identification',
          'Intervention recommendations',
          'Progress tracking',
          'Success forecasting',
          'Retention analytics'
        ],
        stats: { accuracy: '89%', interventions: '2K+', retention: '25% improvement', students: '10K+' },
        applications: ['Student Retention', 'Academic Support', 'Intervention Planning']
      }
    ],
    mobile: [
      {
        id: 7,
        title: 'Adam Mobile Learning App',
        description: 'Mobile-first learning application with offline access, enabling students to learn anytime, anywhere.',
        image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'Offline learning access',
          'Push notifications',
          'Cross-device sync',
          'Micro-learning modules',
          'Progress tracking',
          'Mobile-optimized content'
        ],
        stats: { downloads: '25K+', rating: '4.7/5', countries: '40+', languages: '3' },
        applications: ['Mobile Learning', 'On-the-go Education', 'Micro-learning']
      },
      {
        id: 8,
        title: 'Academic Mobile Suite for Instructors',
        description: 'Comprehensive mobile tools for instructors to manage courses, engage with students, and track academic progress.',
        image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'Course management',
          'Student communication',
          'Grade tracking',
          'Attendance monitoring',
          'Content sharing',
          'Announcements'
        ],
        stats: { instructors: '500+', engagement: '+45%', satisfaction: '94%', active: 'daily' },
        applications: ['Teaching', 'Course Administration', 'Student Engagement']
      }
    ],
    virtual: [
      {
        id: 9,
        title: 'Virtual Learning Environment',
        description: 'Immersive virtual learning environment for remote education and collaborative academic work.',
        image: 'https://images.unsplash.com/photo-1593508512255-86ab42a8e620?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'Virtual classrooms',
          'Collaborative workspaces',
          'Resource libraries',
          'Discussion forums',
          'Assignment submission',
          'Peer review tools'
        ],
        stats: { students: '12K+', courses: '300+', engagement: '3.2x higher', satisfaction: '92%' },
        applications: ['Remote Learning', 'Collaborative Education', 'Distance Programs']
      },
      {
        id: 10,
        title: 'Academic Webinar Platform',
        description: 'Specialized platform for academic webinars, conferences, and guest lectures with interactive features.',
        image: 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
        features: [
          'Live streaming',
          'Q&A sessions',
          'Polling',
          'Recording',
          'Attendance tracking',
          'Certificate generation'
        ],
        stats: { webinars: '500+', attendees: '50K+', experts: '200+', countries: '35+' },
        applications: ['Academic Conferences', 'Guest Lectures', 'Research Seminars']
      }
    ]
  };

  // Technology statistics
  const techStats = [
    { number: '50+', label: 'Partner Institutions', icon: '🏛️' },
    { number: '15K+', label: 'Students Served', icon: '👥' },
    { number: '99.9%', label: 'Platform Uptime', icon: '⚡' },
    { number: '24/7', label: 'Technical Support', icon: '🕐' }
  ];

  // Academic technology partners
  const techPartners = [
    { name: 'Sabahattin Zaim University', logo: 'https://via.placeholder.com/150x50?text=SZÜ' },
    { name: 'Medipol University', logo: 'https://via.placeholder.com/150x50?text=Medipol' },
    { name: 'American International Theism University', logo: 'https://via.placeholder.com/150x50?text=AITU' },
    { name: 'University of 28 May (Algeria)', logo: 'https://via.placeholder.com/150x50?text=28May' },
    { name: 'ABC Horizon University', logo: 'https://via.placeholder.com/150x50?text=ABC' }
  ];

  // Digital learning formats
  const digitalFormats = [
    'Self-Paced eLearning',
    'Blended Learning',
    'MOOCs',
    'Virtual Classrooms',
    'Mobile Learning',
    'Technical Webinars'
  ];

  return (
    <div className="technology-page">
      {/* Hero Section - Updated */}
      <section className="tech-hero">
        <div className="hero-content">
          <div className='hero-text'>
            <h1>Academic Technology Solutions</h1>
            <p>Innovative digital platforms and tools for modern education and capacity development</p>
            <div className="hero-tags">
              {digitalFormats.slice(0, 4).map((format, index) => (
                <span key={index} className="hero-tag">{format}</span>
              ))}
            </div>
          </div>
          <div className="hero-stats">
            {techStats.map((stat, index) => (
              <div key={index} className="stat">
                <span className="stat-icon">{stat.icon}</span>
                <span className="number">{stat.number}</span>
                <span className="label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Formats Section */}
      <section 
        className="innovation-areas"
        ref={el => sectionRefs.current.innovation = el}
        data-section="innovation"
      >
        <div className={`container ${visibleSections.innovation ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Digital Learning Formats</h2>
            <p>Flexible educational delivery methods for diverse learning needs</p>
          </div>
          <div className="innovation-grid">
            {digitalFormats.map((format, index) => (
              <div key={index} className="innovation-item">
                <div className="innovation-icon">
                  {index === 0 ? '⏱️' : index === 1 ? '🔄' : index === 2 ? '🌍' : 
                   index === 3 ? '🎥' : index === 4 ? '📱' : '🔧'}
                </div>
                <span>{format}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Categories */}
      <section 
        className="tech-categories"
        ref={el => sectionRefs.current.categories = el}
        data-section="categories"
      >
        <div className={`container ${visibleSections.categories ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Our Academic Technology Solutions</h2>
            <p>Comprehensive suite of educational technology platforms</p>
          </div>
          <div className="categories-tabs">
            {techCategories.map(category => (
              <button
                key={category.id}
                className={`category-tab ${activeTab === category.id ? 'active' : ''}`}
                onClick={() => setActiveTab(category.id)}
                aria-label={`View ${category.name} solutions`}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-name">{category.name}</span>
                <span className="category-desc">{category.description}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Solutions */}
      <section 
        className="tech-solutions"
        ref={el => sectionRefs.current.solutions = el}
        data-section="solutions"
      >
        <div className={`container ${visibleSections.solutions ? 'visible' : ''}`}>
          <div className="solutions-grid">
            {techSolutions[activeTab].map(solution => (
              <div key={solution.id} className="solution-card">
                <div className="solution-image">
                  <img src={solution.image} alt={solution.title} loading="lazy" />
                </div>
                <div className="solution-content">
                  <h3>{solution.title}</h3>
                  <p>{solution.description}</p>
                  
                  <div className="solution-applications">
                    {solution.applications.map((app, idx) => (
                      <span key={idx} className="application-tag">{app}</span>
                    ))}
                  </div>
                  
                  <div className="solution-features">
                    <h4>Key Features</h4>
                    <ul>
                      {solution.features.slice(0, 5).map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="solution-stats">
                    {Object.entries(solution.stats).map(([key, value]) => (
                      <div key={key} className="stat-item">
                        <span className="stat-value">{value}</span>
                        <span className="stat-label">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Partners */}
      <section 
        className="tech-partners"
        ref={el => sectionRefs.current.partners = el}
        data-section="partners"
      >
        <div className={`container ${visibleSections.partners ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Academic & Technology Partners</h2>
            <p>Collaborating with leading universities to advance educational technology</p>
          </div>
          <div className="partners-grid">
            {techPartners.map((partner, index) => (
              <div key={index} className="partner-item">
                <img src={partner.logo} alt={partner.name} loading="lazy" />
                <span>{partner.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section 
        className="tech-case-studies"
        ref={el => sectionRefs.current.caseStudies = el}
        data-section="caseStudies"
      >
        <div className={`container ${visibleSections.caseStudies ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Success Stories</h2>
            <p>Real-world implementations in academic institutions</p>
          </div>
          <div className="case-studies-grid">
            <div className="case-study">
              <div className="case-image">
                <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80" alt="University Implementation" loading="lazy" />
              </div>
              <div className="case-content">
                <h3>Multi-University LMS Implementation</h3>
                <p>Adam LMS deployed across 5 partner universities in Turkey, Algeria, and Bahrain, enabling multilingual education for 15,000+ students and faculty collaboration.</p>
                <div className="case-stats">
                  <div className="stat">
                    <span className="number">15K+</span>
                    <span className="label">Students</span>
                  </div>
                  <div className="stat">
                    <span className="number">3</span>
                    <span className="label">Languages</span>
                  </div>
                  <div className="stat">
                    <span className="number">5</span>
                    <span className="label">Universities</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="case-study">
              <div className="case-image">
                <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80" alt="Research Collaboration" loading="lazy" />
              </div>
              <div className="case-content">
                <h3>Research Collaboration Platform</h3>
                <p>Virtual research environment connecting scholars from 15 countries for collaborative research on sustainable development and capacity building.</p>
                <div className="case-stats">
                  <div className="stat">
                    <span className="number">15</span>
                    <span className="label">Countries</span>
                  </div>
                  <div className="stat">
                    <span className="number">200+</span>
                    <span className="label">Researchers</span>
                  </div>
                  <div className="stat">
                    <span className="number">45+</span>
                    <span className="label">Publications</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="tech-cta"
        ref={el => sectionRefs.current.cta = el}
        data-section="cta"
      >
        <div className={`container ${visibleSections.cta ? 'visible' : ''}`}>
          <div className="cta-content">
            <h2>Transform Your Academic Institution</h2>
            <p>Discover how our technology solutions can enhance your educational programs</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn primary">Schedule a Demo</Link>
              <Link to="/partners" className="btn outline">Partner With Us</Link>
            </div>
            <p className="cta-contact">📞 (+90) 380-772631 | ✉️ tech@adaminternationalacademy.com</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Technology;